import { Box, Button, CircularProgress, Collapse, Typography } from '@mui/material';
import React from 'react';

import { SectionLabel } from './SectionLabel';
import { DropdownItem, SelectMenu } from '../../../../components/atoms/SelectMenu';
import { serviceTypeMappings } from '../../../../utils/services';
import { SECTION_LABEL_SX } from '../constants';
import { CreateServiceFormValues, ServiceTypeOptions } from '../types';

export interface ServiceTypeFieldsProps {
  values: CreateServiceFormValues;
  options: ServiceTypeOptions;
  loading: boolean;
  showOverride: boolean;
  onToggleOverride: (next: boolean) => void;
  onMappingChange: (value: string) => void;
  onChange: (patch: Partial<CreateServiceFormValues>) => void;
  validate: boolean;
  error?: string;
  disabled?: boolean;
  lockReason?: string;
  locked?: boolean;
}

export const ServiceTypeFields: React.FC<ServiceTypeFieldsProps> = ({
  values,
  options,
  loading,
  showOverride,
  onToggleOverride,
  onMappingChange,
  onChange,
  validate,
  error,
  disabled,
  lockReason,
  locked,
}) => {
  const mappingItems: DropdownItem[] = serviceTypeMappings(options.mappings) ?? [];
  const effectiveDisabled = disabled || !!lockReason || !!locked;

  if (loading) {
    return (
      <Box>
        <Typography sx={SECTION_LABEL_SX}>Service Type *</Typography>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <SectionLabel
        label="Service Type"
        required
        lockReason={lockReason}
        locked={locked}
        helper={!showOverride && mappingItems.length === 0 ? 'No Mappings Available' : undefined}
      >
        {!showOverride ? (
          <SelectMenu
            id="create-service-type-mapping"
            label={mappingItems.length ? 'Service Type Mapping' : 'No Mappings Available'}
            selectedValue={values.serviceTypeMapping}
            onChange={onMappingChange}
            items={mappingItems}
            disabled={effectiveDisabled || !mappingItems.length}
            validate={validate && !values.serviceTypeMapping}
            hideInlineLabel
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <SelectMenu
              id="create-service-type"
              label="Service Type"
              selectedValue={values.serviceTypeCode}
              onChange={(v: string) => onChange({ serviceTypeCode: v })}
              items={options.serviceTypes}
              validate={validate && !values.serviceTypeCode}
              disabled={effectiveDisabled}
              hideInlineLabel
            />
            <SelectMenu
              id="create-service-action"
              label="Action Type"
              selectedValue={values.actionTypeCode}
              onChange={(v: string) => onChange({ actionTypeCode: v })}
              items={options.actionTypes}
              validate={validate && !values.actionTypeCode}
              disabled={effectiveDisabled}
              hideInlineLabel
            />
            <SelectMenu
              id="create-service-process"
              label="Process Type"
              selectedValue={values.processTypeCode}
              onChange={(v: string) => onChange({ processTypeCode: v })}
              items={options.processTypes}
              validate={validate && !values.processTypeCode}
              disabled={effectiveDisabled}
              hideInlineLabel
            />
          </Box>
        )}
      </SectionLabel>
      <Collapse in={!!error} timeout={0}>
        <Typography sx={{ fontSize: '12px', color: 'error.main' }}>{error}</Typography>
      </Collapse>
      <Button
        size="small"
        onClick={() => onToggleOverride(!showOverride)}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          fontSize: '13px',
          color: 'primary.main',
          p: 0,
          minWidth: 0,
        }}
        disabled={effectiveDisabled}
      >
        {showOverride ? 'Back to mappings' : 'Edit service, action or process'}
      </Button>
    </Box>
  );
};
